/**
 * Which workstreams the catalog *banner* previews, and the total activity count.
 *
 * Deliberately NOT derived from `./catalog` at runtime. Reaching for it here would
 * drag the whole taxonomy back into the initial draft chunk.
 *
 * Those labels are editorial rather than the raw L2 names, to prevent long labels.
 */

/** One workstream's folder in the banner strip. */
export interface ICatalogBannerWorkstream {
  /** The catalog's own workstream string. */
  workstream: string;
  /** i18n key for the folder tab's label — the design's short form. */
  labelKey: string;
  /** i18n keys for the three process-group pills, in fan order: left, centre, right. */
  subKeys: readonly [string, string, string];
}

/**
 * Workstreams the banner shows. Stored in catalog order (descending by activity
 * count); `CatalogBanner` sorts them alphabetically by translated label.
 */
export const CATALOG_BANNER_WORKSTREAMS: readonly ICatalogBannerWorkstream[] = [
  {
    workstream: 'BIAN · Corporate Banking Products',
    labelKey: 'drafts.processCatalog.banner.bian-corporate.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-corporate.sub1',
      'drafts.processCatalog.banner.bian-corporate.sub2',
      'drafts.processCatalog.banner.bian-corporate.sub3',
    ],
  },
  {
    workstream: 'Finance · Record-to-Report',
    labelKey: 'drafts.processCatalog.banner.record-to-report.label',
    subKeys: [
      'drafts.processCatalog.banner.record-to-report.sub1',
      'drafts.processCatalog.banner.record-to-report.sub2',
      'drafts.processCatalog.banner.record-to-report.sub3',
    ],
  },
  {
    workstream: 'BIAN · Lending',
    labelKey: 'drafts.processCatalog.banner.bian-lending.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-lending.sub1',
      'drafts.processCatalog.banner.bian-lending.sub2',
      'drafts.processCatalog.banner.bian-lending.sub3',
    ],
  },
  {
    workstream: 'BIAN · Retail Banking and Consumer',
    labelKey: 'drafts.processCatalog.banner.bian-retail.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-retail.sub1',
      'drafts.processCatalog.banner.bian-retail.sub2',
      'drafts.processCatalog.banner.bian-retail.sub3',
    ],
  },
  {
    workstream: 'Finance · Procure-to-Pay / AP',
    labelKey: 'drafts.processCatalog.banner.procure-to-pay.label',
    subKeys: [
      'drafts.processCatalog.banner.procure-to-pay.sub1',
      'drafts.processCatalog.banner.procure-to-pay.sub2',
      'drafts.processCatalog.banner.procure-to-pay.sub3',
    ],
  },
  {
    workstream: 'BIAN · Payments',
    labelKey: 'drafts.processCatalog.banner.bian-payments.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-payments.sub1',
      'drafts.processCatalog.banner.bian-payments.sub2',
      'drafts.processCatalog.banner.bian-payments.sub3',
    ],
  },
  {
    workstream: 'BIAN · Business Development',
    labelKey: 'drafts.processCatalog.banner.bian-business.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-business.sub1',
      'drafts.processCatalog.banner.bian-business.sub2',
      'drafts.processCatalog.banner.bian-business.sub3',
    ],
  },
  {
    workstream: 'Finance · Order-to-Cash / AR',
    labelKey: 'drafts.processCatalog.banner.order-to-cash.label',
    subKeys: [
      'drafts.processCatalog.banner.order-to-cash.sub1',
      'drafts.processCatalog.banner.order-to-cash.sub2',
      'drafts.processCatalog.banner.order-to-cash.sub3',
    ],
  },
  {
    workstream: 'Finance · Treasury & Cash Mgmt',
    labelKey: 'drafts.processCatalog.banner.treasury.label',
    subKeys: [
      'drafts.processCatalog.banner.treasury.sub1',
      'drafts.processCatalog.banner.treasury.sub2',
      'drafts.processCatalog.banner.treasury.sub3',
    ],
  },
  {
    workstream: 'Finance · Financial Planning & Analysis',
    labelKey: 'drafts.processCatalog.banner.fpa.label',
    subKeys: [
      'drafts.processCatalog.banner.fpa.sub1',
      'drafts.processCatalog.banner.fpa.sub2',
      'drafts.processCatalog.banner.fpa.sub3',
    ],
  },
  {
    workstream: 'BIAN · Card Products',
    labelKey: 'drafts.processCatalog.banner.bian-cards.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-cards.sub1',
      'drafts.processCatalog.banner.bian-cards.sub2',
      'drafts.processCatalog.banner.bian-cards.sub3',
    ],
  },
  {
    workstream: 'Finance · Tax Compliance',
    labelKey: 'drafts.processCatalog.banner.tax.label',
    subKeys: [
      'drafts.processCatalog.banner.tax.sub1',
      'drafts.processCatalog.banner.tax.sub2',
      'drafts.processCatalog.banner.tax.sub3',
    ],
  },
  {
    workstream: 'Finance · Payroll',
    labelKey: 'drafts.processCatalog.banner.payroll.label',
    subKeys: [
      'drafts.processCatalog.banner.payroll.sub1',
      'drafts.processCatalog.banner.payroll.sub2',
      'drafts.processCatalog.banner.payroll.sub3',
    ],
  },
  {
    workstream: 'BIAN · Bank Relations',
    labelKey: 'drafts.processCatalog.banner.bian-relations.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-relations.sub1',
      'drafts.processCatalog.banner.bian-relations.sub2',
      'drafts.processCatalog.banner.bian-relations.sub3',
    ],
  },
  {
    workstream: 'BIAN · Channels',
    labelKey: 'drafts.processCatalog.banner.bian-channels.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-channels.sub1',
      'drafts.processCatalog.banner.bian-channels.sub2',
      'drafts.processCatalog.banner.bian-channels.sub3',
    ],
  },
  {
    workstream: 'BIAN · Wealth',
    labelKey: 'drafts.processCatalog.banner.bian-wealth.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-wealth.sub1',
      'drafts.processCatalog.banner.bian-wealth.sub2',
      'drafts.processCatalog.banner.bian-wealth.sub3',
    ],
  },
  {
    workstream: 'BIAN · Product and Price',
    labelKey: 'drafts.processCatalog.banner.bian-products.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-products.sub1',
      'drafts.processCatalog.banner.bian-products.sub2',
      'drafts.processCatalog.banner.bian-products.sub3',
    ],
  },
  {
    workstream: 'BIAN · Corporate Finance',
    labelKey: 'drafts.processCatalog.banner.bian-corporate-finance.label',
    subKeys: [
      'drafts.processCatalog.banner.bian-corporate-finance.sub1',
      'drafts.processCatalog.banner.bian-corporate-finance.sub2',
      'drafts.processCatalog.banner.bian-corporate-finance.sub3',
    ],
  },
];

/** Total L3 activities in the catalog. */
export const CATALOG_BANNER_ACTIVITY_COUNT = 536;
